import { API } from '../services/amplifyCompat';
import { useGetCurrentUserProfile } from './use-get-current-user-profile.hook';
import { useUserInfoContext } from './use-user-info-context';
import { getAllSubmittedActionsForUser, getAllUsers } from '../graphql/queries';
import { useEffect, useState } from 'react';
import { avatarOptions } from '../constants/avatar-options';
import useTranslation from '../components/customHooks/translations';
import { captureException, startSpan } from '../monitoring/sentry';
import { isTransientNetworkError } from '../utils/network-retry';

const defaultStats = {
  total_points: 0,
  weekly_points: 0,
  number_of_actions: 0,
  total_co2: 0,
  weekly_co2: 0,
  streak: 0,
}
const defaultActions = {
  actions_by_id: []
}
const defaultActionById = {
  action_name: '', 
  avatar: null,   
  co2_saved: 0,
  points_earned: 0,
  number: 0
}
const defaultRankings = {
  user: null,
  all_users: [],
}
const defaultImpact = {
  weekly_co2: 0,
  actions_by_month: []
}
const defaultActionByDay = {
  date: null,
  total_co2_saved: 0
}

function reduceActionsById(actions, avatar){
  if(!actions.length > 0) return [];
  return actions.reduce((acc, action) => {
    if (!action.is_rejected && !acc[action.action_id]) {
      acc[action.action_id] = {
        ...defaultActionById,
        action_name: action.action_name, 
        avatar: avatar.actions.find(act => act.id === action.action_id)?.icon || avatar.imageUrl,
        id: action.action_id,
      };
    }
    if(!action.is_rejected){
      acc[action.action_id].co2_saved += action.g_co2_saved;
      acc[action.action_id].points_earned += action.points_earned;
      acc[action.action_id].number += 1;
    }
    return acc;
  }, {});
}
function countActions(actions){
  let oneWeekAgo = new Date()
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  let countValidatedWeek = 0;
  let countNotValidatedWeek = 0;
  let countAll = 0;
  actions.forEach(action => {
    if(!action.is_rejected){
      countAll++;
      const actionDate = new Date(action.time_submitted);
      if (actionDate >= oneWeekAgo) {
        if (action.is_validated) {
          countValidatedWeek++;
        } else {
          countNotValidatedWeek++;
        }
      }
    }
  });

  return {
    weekly_validated: countValidatedWeek,
    weekly_unvalidated: countNotValidatedWeek,
    number_of_actions: countAll
  };

}
/** Local calendar YYYY-MM-DD from a Date or ISO timestamp (avoids UTC day shift). */
function toLocalDateKey(value) {
  if (!value) return '';
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value.split('T')[0]) &&
    !value.includes('T')
  ) {
    return value.split('T')[0];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parse YYYY-MM-DD as a local calendar date (not UTC midnight). */
function parseLocalDateKey(dateKey) {
  const [y, m, d] = String(dateKey).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function daysBetweenLocalKeys(startKey, endKey) {
  const msPerDay = 1000 * 3600 * 24;
  return Math.round(
    (parseLocalDateKey(endKey) - parseLocalDateKey(startKey)) / msPerDay
  );
}

function reduceActionsByDay(actions){
  const result = actions.reduce((acc, action) => {
    // Bucket by local calendar day of submission
    const date = toLocalDateKey(action.time_submitted);
    if(!action.is_rejected && date) {
      if (!acc[date]) {
        acc[date] = {
          ...defaultActionByDay,
          date,
        };
      }
      acc[date].total_co2_saved += action.g_co2_saved;
    }
    return acc;
  }, {});

  return Object.values(result);
}
function reduceActionsByMonth(actions){
  const currentYear = new Date().getFullYear();
  let monthValues = new Array(12).fill(0);
  actions.forEach(action => {
    if(!action.is_rejected) {
      // Local year/month so year-end wrap matches user timezone
      const date = new Date(action.time_submitted);
      const year = date.getFullYear();
      const month = date.getMonth(); 
  
      if (year === currentYear) {
        monthValues[month] += action.g_co2_saved;
      }
    }
  });

  return monthValues;
}
function findLongestStreak(actions){
  if (!actions.length) return 0;
  const sortedDays = [...actions].sort(
    (a, b) => parseLocalDateKey(a.date) - parseLocalDateKey(b.date)
  );

  let maxStreak = 0;
  let currentStreak = 1;
  let previousDateKey = sortedDays[0].date;

  for (let i = 1; i < sortedDays.length; i++) {
    const currentDateKey = sortedDays[i].date;
    const differenceInDays = daysBetweenLocalKeys(previousDateKey, currentDateKey);
    if (differenceInDays === 1) {
      currentStreak++;
    } else if (differenceInDays > 1) {
      currentStreak = 1;
    }
    if (currentStreak > maxStreak) {
      maxStreak = currentStreak;
    }
    previousDateKey = currentDateKey;
  }

  return Math.max(maxStreak, currentStreak);
}
function findCurrentStreak(actions) {
  if (actions.length === 0) return 0;

  const sortedDays = [...actions].sort(
    (a, b) => parseLocalDateKey(a.date) - parseLocalDateKey(b.date)
  );
  
  let currentStreak = 1;
  let previousDateKey = sortedDays[0].date;

  for (let i = 1; i < sortedDays.length; i++) {
    const currentDateKey = sortedDays[i].date;
    const differenceInDays = daysBetweenLocalKeys(previousDateKey, currentDateKey);
    if (differenceInDays === 1) {
      currentStreak++;
    } else if (differenceInDays > 1) {
      currentStreak = 1;
    }
    previousDateKey = currentDateKey;
  }

  const todayKey = toLocalDateKey(new Date());
  const mostRecentKey = sortedDays[sortedDays.length - 1].date;
  const differenceToToday = daysBetweenLocalKeys(mostRecentKey, todayKey);

  // Streak stays active if last action was today or yesterday (local)
  if (differenceToToday > 1) {
    currentStreak = 0;
  }

  return currentStreak;
}
export const useCurrentUserDashboard = () => {

  const { user } = useUserInfoContext();
  const { userProfile } = useGetCurrentUserProfile();

  const [actions, setActions] = useState(defaultActions);
  const [stats, setStats] = useState(defaultStats);
  const [rankings, setRankings] = useState(defaultRankings);
  const [impact, setImpact] = useState(defaultImpact);
  
  useEffect(() => {
    async function getApiData(){
      if(user){
        try {
          await startSpan(
            {
              op: 'dashboard.load',
              name: 'Load current user dashboard',
            },
            async (span) => {
              span.setAttribute('user.id', user.user_id?.toString?.() ?? 'unknown');
              const [actionRes, usersRes] = await Promise.all([
                API.graphql({
                  query: getAllSubmittedActionsForUser,
                  variables: { user_id: user.user_id },
                  suppressTransientNetworkErrorCapture: true,
                }),
                API.graphql({
                  query: getAllUsers,
                  suppressTransientNetworkErrorCapture: true,
                }),
              ]);
              const allActions = actionRes?.data?.getAllSubmittedActionsForUser;
              const allUsers = usersRes?.data?.getAllUsers;
              if(allActions && allActions.length > 0) {
                const avatar = avatarOptions.find(avatar => avatar.id === userProfile?.avatar) || avatarOptions[0];
                const { weekly_validated, weekly_unvalidated, number_of_actions} = countActions(allActions);
                const actions_by_id = reduceActionsById(allActions, avatar);
                setActions(prev =>
                  ({
                    ...prev,
                    weekly_validated,
                    weekly_unvalidated,
                    weekly_number_of_actions: weekly_validated + weekly_unvalidated,
                    actions_by_id
                  })
                );
                const actions_by_month = reduceActionsByMonth(allActions);
                setImpact(prev =>
                  ({
                    ...prev,
                    weekly_co2: user.weekly_co2,
                    actions_by_month
                  })
                );
                const actions_by_day = reduceActionsByDay(allActions);
                const longest_streak = findLongestStreak(actions_by_day);
                const current_streak = findCurrentStreak(actions_by_day);

                setStats(prev => ({
                    ...prev,
                    total_points: user.total_points,
                    weekly_points: user.weekly_points,
                    number_of_actions,
                    total_co2: user.total_co2,
                    weekly_co2: user.weekly_co2,
                    streak: current_streak,
                    longest_streak
                  })
                );
              }
              if(allUsers && allUsers.length) {
                const sorted_users = allUsers.sort((a, b) => b.total_points - a.total_points);
                const rank = sorted_users.findIndex(x => user.user_id === x.user_id) + 1;
                const current_user = {
                  ...user,
                  rank
                };
                setRankings(prev =>
                  ({
                    ...prev,
                    user: current_user,
                    all_users: sorted_users
                  })
                );
              }
            }
          );
        } catch (error) {
          if (isTransientNetworkError(error)) {
            return;
          }

          captureException(error, {
            tags: {
              module: 'useCurrentUserDashboard',
              flow: 'dashboard.load',
            },
          });
        }
      }    
    }

    getApiData();
  }, [user, userProfile?.avatar]);

  const translation = useTranslation();
  const greetingText = userProfile?.name
    ? translation.formatString(translation.welcomeName, userProfile.name)
    : `${translation.welcome}!`;

  return {
    greetingText,

    // These will all be arrays or objects of proper data
    actionsObject: actions,
    actions: '(actions)',
    impactObject: impact,
    impact: '(impact)',
    learning: '(learning)',
    notifications: '(notifications)',
    rankingsObject: rankings,
    rankings: '(rankings)',
    statsObject: stats,
    stats: '(stats)'
  };
};
